/**
 * 六爻起卦页
 *
 * 三步流程：
 *   1) 心静过滤：仪式按钮（默认 3 秒倒计时，期间禁用提交）
 *   2) 输入提问 → 服务端起卦（投掷 6 次 + 入库）
 *   3) 流式解卦
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { divinationApi, type DivinationCastResult, type DivinationDetail } from '@/lib/stage3.api';
import { fetchPaywall } from '@/lib/business.api';
import type { PaywallConfigView } from '@guanwei/shared';
import { useToast } from '@/components/ui/Toast';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { SelectField } from '@/components/ui/PremiumControls';
import { BreathingRing, RitualPanel } from '@/components/premium/Experience';
import {
  PremiumReportRenderer,
  ReportPaper,
  parseReportSegments,
} from '@/components/premium/ReportPaper';
import { AnalyzingOverlay } from '@/components/premium/AnalyzingOverlay';
import { listMyCharts, type ChartHistoryItem } from '@/lib/chart.api';
import { ReportPaywallModal } from '@/components/paywall/ReportPaywallModal';

type Phase = 'silence' | 'question' | 'casting' | 'interpret';
type CoinSide = 'front' | 'back';
type CastLine = {
  index: number;
  toss: number;
  coins: CoinSide[];
  label: string;
  lineType: string;
  moving: boolean;
};

function immersiveShellClass(historyCollapsed: boolean) {
  return `mobile-immersive-shell relative mx-auto grid h-full min-h-0 w-full max-w-[1680px] overflow-hidden rounded-2xl border border-border bg-bg-1 shadow-gold lg:rounded-3xl ${
    historyCollapsed
      ? 'grid-cols-[minmax(0,1fr)] lg:grid-cols-[0_minmax(0,1fr)]'
      : 'grid-cols-[minmax(0,1fr)] lg:grid-cols-[360px_minmax(0,1fr)]'
  }`;
}

function immersiveHistoryClass(historyCollapsed: boolean) {
  return `mobile-immersive-history absolute inset-y-0 left-0 z-30 h-full w-[min(82vw,360px)] min-h-0 overflow-y-auto border-r border-border bg-bg p-4 shadow-gold transition-transform duration-base lg:relative lg:z-auto lg:w-auto lg:shadow-none lg:p-8 ${
    historyCollapsed
      ? '-translate-x-full lg:invisible lg:p-0 lg:opacity-0'
      : 'translate-x-0 lg:visible lg:opacity-100'
  }`;
}

function HistoryToggle({ collapsed, onClick }: { collapsed: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute left-3 top-3 z-40 inline-flex items-center gap-2 rounded-full border border-border-accent bg-accent px-4 py-2 text-sm font-medium text-accent-fg shadow-gold transition-all hover:bg-accent-hover active:bg-accent-active lg:left-4 lg:top-4"
      aria-label={collapsed ? '查看历史记录' : '隐藏历史记录'}
    >
      <span aria-hidden="true">☰</span>
      <span>{collapsed ? '查看历史' : '隐藏历史'}</span>
    </button>
  );
}

function formatChartOptionDescription(chart: ChartHistoryItem) {
  const gender = chart.gender === 'male' ? '男' : chart.gender === 'female' ? '女' : '未指定';
  return `${gender} · ${chart.baziKey}`;
}

function statusLabel(status: string) {
  const map: Record<string, string> = {
    pending: '等待中',
    streaming: '生成中',
    completed: '已完成',
    failed: '失败',
    cast: '已成卦',
    interpreted: '已解读',
  };
  return map[status] ?? status;
}

function coinSidesFromToss(toss: number): CoinSide[] {
  const frontCount = Math.max(0, Math.min(3, toss - 6));
  return Array.from({ length: 3 }, (_, index) => (index < frontCount ? 'front' : 'back'));
}

function lineTypeFromToss(toss: number) {
  const map: Record<number, { label: string; lineType: string; moving: boolean }> = {
    6: { label: '老阴', lineType: '阴爻变', moving: true },
    7: { label: '少阳', lineType: '阳爻', moving: false },
    8: { label: '少阴', lineType: '阴爻', moving: false },
    9: { label: '老阳', lineType: '阳爻变', moving: true },
  };
  return map[toss] ?? { label: '未知', lineType: '未定', moving: false };
}

function buildCastLines(tosses: number[]): CastLine[] {
  return tosses.map((toss, index) => ({
    index: index + 1,
    toss,
    coins: coinSidesFromToss(toss),
    ...lineTypeFromToss(toss),
  }));
}

export function DivinationPage() {
  const showToast = useToast();
  const [phase, setPhase] = useState<Phase>('silence');
  const [historyCollapsed, setHistoryCollapsed] = useState(() =>
    typeof window === 'undefined' ? false : window.innerWidth < 1024,
  );
  const [silenceLeft, setSilenceLeft] = useState(3);
  const [charts, setCharts] = useState<ChartHistoryItem[]>([]);
  const [chartId, setChartId] = useState('');
  const [loadingCharts, setLoadingCharts] = useState(true);
  const [question, setQuestion] = useState('');
  const [cast, setCast] = useState<DivinationCastResult | null>(null);
  const [revealedLines, setRevealedLines] = useState(0);
  const [activeLine, setActiveLine] = useState(0);
  const [coinSpinKey, setCoinSpinKey] = useState(0);
  const [interpretation, setInterpretation] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paywall, setPaywall] = useState<PaywallConfigView | null>(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [history, setHistory] = useState<DivinationDetail[]>([]);
  const [loadingHistoryId, setLoadingHistoryId] = useState<string | null>(null);
  const castLines = useMemo(() => buildCastLines(cast?.tosses ?? []), [cast?.tosses]);

  useEffect(() => {
    void listMyCharts()
      .then((list) => {
        setCharts(list);
        if (list.length > 0) setChartId(list[0].id);
      })
      .catch(() => setCharts([]))
      .finally(() => setLoadingCharts(false));
  }, []);

  useEffect(() => {
    void fetchPaywall('divination.start')
      .then(setPaywall)
      .catch(() => setPaywall(null));
    void divinationApi
      .list()
      .then((list) => setHistory(list as DivinationDetail[]))
      .catch(() => setHistory([]));
  }, []);

  // 心静倒计时
  useEffect(() => {
    if (phase !== 'silence' || silenceLeft <= 0) return;
    const t = setTimeout(() => setSilenceLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [silenceLeft, phase]);

  useEffect(() => {
    if (phase !== 'casting' || !cast) return;
    if (revealedLines >= cast.tosses.length) {
      const timer = setTimeout(() => {
        setActiveLine(0);
        setPhase('interpret');
        setStreaming(true);
        void divinationApi.interpret(cast.id, {
          onDelta: (d) => setInterpretation((b) => b + d),
          onDone: () => setStreaming(false),
          onError: (e) => {
            setError(e.message);
            setStreaming(false);
          },
        });
      }, 850);
      return () => clearTimeout(timer);
    }

    const nextLine = revealedLines + 1;
    setActiveLine(nextLine);
    setCoinSpinKey((key) => key + 1);
    const timer = setTimeout(() => setRevealedLines(nextLine), 1150);
    return () => clearTimeout(timer);
  }, [cast, phase, revealedLines]);

  const onCast = async () => {
    if (!question.trim() || question.trim().length < 4) {
      showToast('请输入至少 4 个字符的提问', 'error');
      return;
    }
    if (!chartId) {
      showToast('请先选择命盘', 'error');
      return;
    }
    setError(null);
    setPhase('casting');
    try {
      const r = await divinationApi.cast(question.trim(), chartId);
      setCast(r);
      setRevealedLines(0);
      setActiveLine(1);
      setCoinSpinKey((key) => key + 1);
      setInterpretation('');
      setStreaming(false);
      void divinationApi
        .list()
        .then((list) => setHistory(list as DivinationDetail[]))
        .catch(() => undefined);
    } catch (e) {
      const err = e as Error & { code?: string };
      setPhase('question');
      if (
        err.code === 'PAYWALL_REQUIRED' ||
        err.code === 'QUOTA_EXHAUSTED' ||
        err.message.includes('PAYWALL_REQUIRED') ||
        err.message.includes('QUOTA_EXHAUSTED')
      ) {
        setPaywallOpen(true);
        setError('起卦需要购买单次服务或使用会员权益');
      } else {
        setError(err.message);
      }
    }
  };

  const openHistory = async (id: string) => {
    setLoadingHistoryId(id);
    setError(null);
    try {
      const detail = await divinationApi.detail(id);
      setQuestion(detail.question);
      setCast({
        id: detail.id,
        tosses: detail.tosses,
        benGua: detail.benGua as DivinationCastResult['benGua'],
        bianGua: detail.bianGua as DivinationCastResult['bianGua'],
        huGua: detail.huGua as DivinationCastResult['huGua'],
        dongYao: detail.dongYao,
        quota: { used: 0, remaining: 0, unlimited: false },
      });
      setRevealedLines(detail.tosses.length);
      setActiveLine(0);
      setInterpretation(detail.interpretation ?? '');
      setStreaming(false);
      setPhase('interpret');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingHistoryId(null);
    }
  };

  const shellClass = useMemo(() => immersiveShellClass(historyCollapsed), [historyCollapsed]);
  const historyClass = useMemo(() => immersiveHistoryClass(historyCollapsed), [historyCollapsed]);

  const historyPanel = (
    <aside className={historyClass}>
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-accent">History</p>
          <h2 className="mt-2 font-serif text-2xl text-fg">历史起卦</h2>
        </div>
        <Button size="sm" variant="ghost" onClick={() => setPhase('question')}>
          新起卦
        </Button>
      </div>
      {history.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-bg-2 p-5 text-sm leading-7 text-fg-muted">
          暂无起卦记录。
        </div>
      ) : (
        <div className="space-y-3">
          {history.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => void openHistory(item.id)}
              className="w-full rounded-2xl border border-border bg-bg-1 p-4 text-left text-fg-1 transition-all hover:border-accent hover:bg-bg-2"
            >
              <p className="line-clamp-2 text-sm text-fg">{item.question}</p>
              <p className="mt-2 text-xs text-fg-muted">
                {new Date(item.createdAt).toLocaleString()} ·{' '}
                {loadingHistoryId === item.id ? '读取中' : statusLabel(item.status)}
              </p>
            </button>
          ))}
        </div>
      )}
    </aside>
  );

  if (loadingCharts) {
    return (
      <main className="flex-1 flex items-center justify-center px-6 text-sm text-fg-muted">
        正在读取命盘…
      </main>
    );
  }

  if (charts.length === 0) {
    return (
      <main className="flex-1 flex items-center justify-center px-6">
        <Card className="max-w-md p-10 text-center">
          <p className="mb-3 font-serif text-2xl text-fg">六爻起卦</p>
          <p className="mb-6 text-sm leading-7 text-fg-2">
            起卦必须绑定一张命盘，解卦时会把命盘信息一并发送给大模型。
          </p>
          <Link to="/chart">
            <Button size="lg">先去排盘</Button>
          </Link>
        </Card>
      </main>
    );
  }

  return (
    <main className="mobile-immersive-page h-full min-h-0 overflow-hidden px-3 py-3 sm:px-6 lg:px-8 lg:py-6">
      <div className={shellClass}>
        {historyPanel}
        <section className="mobile-immersive-content relative order-2 h-full min-h-0 space-y-6 overflow-y-auto overscroll-contain bg-bg-1 p-4 sm:p-8 lg:space-y-8 lg:p-10">
          <HistoryToggle
            collapsed={historyCollapsed}
            onClick={() => setHistoryCollapsed((value) => !value)}
          />
          <div>
            <p className="text-xs tracking-[0.3em] text-accent">DIVINATION</p>
            <h1 className="mt-2 font-serif text-3xl text-fg">六爻起卦</h1>
            <p className="text-sm text-fg-muted mt-2">
              同一问题主体 24 小时内仅可起卦一次；一次只问一个清晰对象。
            </p>
          </div>

          {phase === 'silence' && (
            <RitualPanel
              eyebrow="SILENCE"
              title="请先让问题沉下来"
              description="起卦前请安静片刻，只保留一个明确的问题主体。这个仪式不是神秘化，而是帮助你减少重复追问和情绪化输入。"
              className="text-center"
            >
              <BreathingRing value={silenceLeft} total={3} />
              <Button
                disabled={silenceLeft > 0}
                onClick={() => setPhase('question')}
                className="mt-6"
              >
                {silenceLeft > 0 ? `${silenceLeft} 秒后可继续` : '我已心静，开始起卦'}
              </Button>
            </RitualPanel>
          )}

          {phase === 'question' && (
            <RitualPanel
              eyebrow="QUESTION"
              title="写下这一次只问的事"
              description="问题越具体，解卦越能落到现实行动。建议包含对象、时间范围和你真正犹豫的点。"
            >
              <SelectField
                label="绑定命盘"
                value={chartId}
                onChange={setChartId}
                placeholder="选择命盘"
                options={charts.map((chart) => ({
                  value: chart.id,
                  label: chart.displayName,
                  description: formatChartOptionDescription(chart),
                }))}
              />
              <textarea
                rows={3}
                className="w-full rounded-xl border border-border bg-bg px-4 py-3 text-sm leading-7 text-fg focus:border-accent focus:outline-none"
                placeholder="如：今年下半年是否适合换工作？"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
              />
              {error && <p className="text-sm text-danger">{error}</p>}
              <Button onClick={onCast} size="lg">
                起卦
              </Button>
            </RitualPanel>
          )}

          {phase === 'casting' && !cast && (
            <RitualPanel
              eyebrow="CASTING"
              title="请稍候，铜钱将起"
              description="正在完成起卦校验与命盘绑定。铜钱落下后，将按六次投掷逐爻展示正反结果。"
              className="text-center"
            >
              <div className="mx-auto flex min-h-[150px] max-w-md items-center justify-center gap-5">
                {(['front', 'back', 'front'] as CoinSide[]).map((coin, index) => (
                  <RitualCoin key={`waiting-${index}`} side={coin} delay={index * 160} />
                ))}
              </div>
              <p className="mt-4 text-sm text-fg-muted">正在准备六爻投掷…</p>
            </RitualPanel>
          )}

          {(phase === 'casting' || phase === 'interpret') && cast && (
            <div className="space-y-6">
              <CoinCastingRitual
                cast={cast}
                lines={castLines}
                question={question}
                revealedLines={revealedLines}
                activeLine={activeLine}
                coinSpinKey={coinSpinKey}
                completed={phase === 'interpret'}
              />

              {phase === 'interpret' && (
                <ReportPaper
                  title="观微先生 · 解卦"
                  subtitle="从卦意、现实映射和行动建议三个层面拆解问题。"
                >
                  {interpretation ? (
                    <PremiumReportRenderer
                      segments={parseReportSegments(interpretation)}
                      streaming={streaming}
                    />
                  ) : streaming ? (
                    <AnalyzingOverlay />
                  ) : (
                    <div className="space-y-3 text-sm text-fg-muted">
                      <p>该卦尚未完成解读。</p>
                      {cast && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setInterpretation('');
                            setStreaming(true);
                            void divinationApi.interpret(cast.id, {
                              onDelta: (d) => setInterpretation((b) => b + d),
                              onDone: () => setStreaming(false),
                              onError: (e) => {
                                setError(e.message);
                                setStreaming(false);
                              },
                            });
                          }}
                        >
                          继续解卦
                        </Button>
                      )}
                    </div>
                  )}
                </ReportPaper>
              )}
            </div>
          )}

          <ReportPaywallModal
            open={paywallOpen}
            scene="divination.start"
            config={paywall}
            fulfillmentMeta={{ chartId }}
            onClose={() => setPaywallOpen(false)}
            dismissText="我再考虑一下"
          />
        </section>
      </div>
    </main>
  );
}

function CoinCastingRitual({
  cast,
  lines,
  question,
  revealedLines,
  activeLine,
  coinSpinKey,
  completed,
}: {
  cast: DivinationCastResult;
  lines: CastLine[];
  question: string;
  revealedLines: number;
  activeLine: number;
  coinSpinKey: number;
  completed: boolean;
}) {
  const active = lines[activeLine - 1];
  const displayCoins = active?.coins ?? ['front', 'back', 'front'];
  return (
    <RitualPanel
      eyebrow={completed ? 'HEXAGRAM' : 'CASTING'}
      title={completed ? '卦象已成' : `第 ${activeLine || 1} 次投掷 · 第 ${activeLine || 1} 爻`}
      description={
        completed
          ? '本卦看当下结构，变卦看趋势变化，互卦看问题内部动力。'
          : '六爻自下而上成象。每一爻由三枚铜钱合成，正面计三，反面计二。'
      }
    >
      <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
        <div className="rounded-3xl border border-border bg-bg/80 p-5 shadow-inner">
          <p className="text-xs uppercase tracking-[0.24em] text-accent">Coin Ledger</p>
          <div className="mt-4 space-y-2">
            {lines
              .map((line) => {
                const visible = line.index <= revealedLines;
                const current = line.index === activeLine && !visible;
                return (
                  <div
                    key={line.index}
                    className={
                      'flex items-center justify-between rounded-2xl border px-3 py-2 text-sm transition-all ' +
                      (current
                        ? 'border-accent bg-accent-soft text-accent shadow-gold'
                        : visible
                          ? 'border-border bg-bg-2 text-fg'
                          : 'border-border bg-bg-1 text-fg-muted')
                    }
                  >
                    <div>
                      <p className="font-medium">
                        第 {line.index} 次 · 第 {line.index} 爻
                      </p>
                      <p className="mt-0.5 text-xs text-fg-muted">
                        {visible
                          ? `${line.label} · ${line.lineType}`
                          : current
                            ? '三钱翻转中'
                            : '待投'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {visible ? (
                        line.coins.map((coin, index) => <MiniCoin key={index} side={coin} />)
                      ) : (
                        <span className="text-xs tracking-widest text-fg-muted">···</span>
                      )}
                    </div>
                  </div>
                );
              })
              .reverse()}
          </div>
          <p className="mt-4 text-xs leading-6 text-fg-muted">
            左侧从上到下展示上爻至初爻，实际成卦顺序为自下而上。
          </p>
        </div>

        <div className="coin-stage relative overflow-hidden rounded-3xl border border-border bg-bg-2 p-6 text-center">
          <div className="coin-stage-grid pointer-events-none absolute inset-0 opacity-60" />
          <div className="relative z-10">
            <p className="text-xs uppercase tracking-[0.32em] text-accent">问事</p>
            <p className="mx-auto mt-3 max-w-2xl font-serif text-xl leading-9 text-fg">
              {question}
            </p>

            {!completed ? (
              <div className="mt-8">
                <div className="mx-auto flex min-h-[150px] max-w-md items-center justify-center gap-5">
                  {displayCoins.map((coin, index) => (
                    <RitualCoin key={`${coinSpinKey}-${index}`} side={coin} delay={index * 140} />
                  ))}
                </div>
                <div className="mx-auto mt-6 h-2 max-w-md overflow-hidden rounded-full bg-bg-1">
                  <div
                    className="h-full rounded-full bg-accent transition-all duration-700"
                    style={{ width: `${Math.max(8, (revealedLines / 6) * 100)}%` }}
                  />
                </div>
                <p className="mt-4 text-sm text-fg-muted">
                  正在记录第 {activeLine} 爻，已成 {revealedLines} / 6 爻
                </p>
              </div>
            ) : (
              <div className="mt-8 space-y-6">
                <HexagramLines lines={lines} />
                <div className="grid gap-4 text-center md:grid-cols-3">
                  <GuaCard title="本卦" name={cast.benGua.name} symbol={cast.benGua.symbol} />
                  <GuaCard title="变卦" name={cast.bianGua.name} symbol={cast.bianGua.symbol} />
                  <GuaCard title="互卦" name={cast.huGua.name} symbol={cast.huGua.symbol} />
                </div>
                <p className="text-xs text-fg-muted">
                  动爻位置：{cast.dongYao.length ? cast.dongYao.join(', ') : '无'} ｜ 投掷：
                  {cast.tosses.join(' ')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </RitualPanel>
  );
}

function RitualCoin({ side, delay }: { side: CoinSide; delay: number }) {
  return (
    <div
      className="ritual-coin relative h-20 w-20 animate-[coin-flip_1.05s_cubic-bezier(0.2,0.72,0.18,1)_both] rounded-full border border-accent bg-accent-soft shadow-gold"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="ritual-coin-inner absolute inset-2 rounded-full border border-border" />
      <div className="absolute inset-0 flex items-center justify-center font-serif text-2xl font-bold text-accent-active">
        {side === 'front' ? '正' : '反'}
      </div>
      <div className="absolute inset-x-5 bottom-3 h-px bg-accent-active opacity-40" />
    </div>
  );
}

function MiniCoin({ side }: { side: CoinSide }) {
  return (
    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-accent bg-accent-soft font-serif text-[10px] text-accent">
      {side === 'front' ? '正' : '反'}
    </span>
  );
}

function HexagramLines({ lines }: { lines: CastLine[] }) {
  return (
    <div className="mx-auto flex max-w-[180px] flex-col-reverse gap-2">
      {lines.map((line) => {
        const yang = line.toss === 7 || line.toss === 9;
        return (
          <div key={line.index} className="flex items-center gap-2">
            <span className="w-10 text-right text-xs text-fg-muted">{line.index}</span>
            <div className="flex flex-1 items-center justify-center gap-2">
              {yang ? (
                <span className="h-2 w-full rounded-full bg-accent" />
              ) : (
                <>
                  <span className="h-2 flex-1 rounded-full bg-fg-muted" />
                  <span className="h-2 flex-1 rounded-full bg-fg-muted" />
                </>
              )}
            </div>
            <span className="w-8 text-left text-xs text-accent">{line.moving ? '变' : ''}</span>
          </div>
        );
      })}
    </div>
  );
}

function GuaCard({ title, name, symbol }: { title: string; name: string; symbol: string }) {
  return (
    <div className="rounded-xl border border-border bg-bg-2 p-5">
      <p className="text-xs text-fg-muted mb-1">{title}</p>
      <p className="font-serif text-4xl text-accent mb-1">{symbol}</p>
      <p className="text-sm text-fg">{name}</p>
    </div>
  );
}
